import { useRef, useState } from 'react';
import storeConnector from '@store/storeConnector';
import FormattedAlias from '../_shared/Translations/FormattedAlias';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Switch,
  Typography,
  Button,
  Alert,
  Snackbar,
  SnackbarCloseReason,
} from '@mui/material';
import { Actions } from '@src/types/Actions';
import { TimeMode } from '@src/types';
import { ServiceState } from '@src/store/reducers/serviceData';
import CloseIcon from '@mui/icons-material/Close';
import Spinner from './Spinner/Spinner';
import AliasSelector from './AliasSelector';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import useExportAliases from '@src/hooks/api/queries/useExportAliases';
import useImportFile, {
  entityName,
} from '@src/hooks/api/mutations/useImportFile';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import deviceSx from '../Home/Collections/configs/DeviceSx';
import { SiteMeta } from '@src/types/SiteMeta';

interface SettingsDialogProps {
  siteMeta: SiteMeta;
  actions: Actions;
  timeMode: TimeMode;
  showMap: ServiceState['showMap'];
  darkTheme: ServiceState['darkTheme'];
  audioNotificationEnabled: ServiceState['audioNotificationEnabled'];
  bannerNotificationEnabled: ServiceState['bannerNotificationEnabled'];
  isOpen: boolean;
  close: () => void;
}

const SettingsDialog = ({
  siteMeta,
  actions,
  timeMode,
  showMap,
  darkTheme,
  audioNotificationEnabled,
  bannerNotificationEnabled,
  isOpen,
  close,
}: SettingsDialogProps) => {
  const Show_Alias_Sets_Select = siteMeta?.ui?.features?.Show_Alias_Sets_Select;
  const [actShowMap] = useState(!!showMap);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { exportAliases, isExporting } = useExportAliases();

  const { importFile, isImporting, error } = useImportFile(
    entityName.ALIAS_SETS,
  );

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleExportClick = async () => {
    await exportAliases();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      importFile(file).then((result) => {
        if (result) {
          setConfirmOpen(true);
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.aliasSets],
          });
        }
      });
    } catch (error) {
      console.error(error);
    }

    // Reset the file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const updateSettings = async (field: string) => {
    const currParams = {
      timeMode: timeMode.toLowerCase(),
      showMap: `${actShowMap}`,
      theme: darkTheme ? 'dark' : 'light',
      audioNotificationEnabled,
      bannerNotificationEnabled,
    };

    setLoading(true);

    switch (field) {
      case 'theme':
        currParams.theme = darkTheme ? 'light' : 'dark';
        if (currParams.theme === 'dark') {
          document.body.classList.remove('theme--light');
          document.body.classList.add('theme--dark');
        } else {
          document.body.classList.remove('theme--dark');
          document.body.classList.add('theme--light');
        }
        break;
      case 'audioNotificationEnabled':
        currParams.audioNotificationEnabled = !audioNotificationEnabled;
        break;
      case 'bannerNotificationEnabled':
        currParams.bannerNotificationEnabled = !bannerNotificationEnabled;
        break;
    }

    const res = await actions.changeUserData(currParams);

    if (!res || (res && res.error)) {
      actions.notifyError(
        `Error saving user settings: ${res.error || 'Params update error'}`,
      );
    }

    actions.updateStore({
      service: {
        ...(field === 'theme' ? { darkTheme: !darkTheme } : {}),
        ...(field === 'audioNotificationEnabled'
          ? {
              audioNotificationEnabled: !audioNotificationEnabled,
            }
          : {}),
        ...(field === 'bannerNotificationEnabled'
          ? {
              bannerNotificationEnabled: !bannerNotificationEnabled,
            }
          : {}),
      },
    });

    setLoading(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          px: 2,
          py: 1,
        },
      }}
    >
      {error?.message && (
        <Alert severity='error' sx={{ mb: 2 }}>
          <FormattedAlias id={error?.message} />
        </Alert>
      )}
      <Snackbar
        open={confirmOpen}
        autoHideDuration={4000}
        onClose={handleClose}
        sx={deviceSx.confirm}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity='success'
          variant='filled'
          sx={deviceSx.confirm.alert}
        >
          <FormattedAlias id='Alias sets successfully updated' />
        </Alert>
      </Snackbar>
      <Stack
        direction={'row'}
        justifyContent='space-between'
        alignItems={'center'}
      >
        <DialogTitle>
          <FormattedAlias id={'Settings'} />
        </DialogTitle>
        <IconButton onClick={close}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider
        sx={{
          border: '1px solid rgb(217, 217, 217)',
        }}
      />
      <DialogContent>
        {loading && <Spinner cover='container' />}

        <Stack direction={'column'} spacing={1}>
          <Stack
            direction={'row'}
            spacing={2}
            justifyContent='space-between'
            alignItems={'center'}
          >
            <FormattedAlias id={'Theme dark mode'} />
            <Switch
              color='secondary'
              onChange={() => updateSettings('theme')}
              checked={darkTheme}
            />
          </Stack>
          <Stack
            direction={'row'}
            spacing={2}
            justifyContent='space-between'
            alignItems={'center'}
          >
            <FormattedAlias id={'Audio notifications'} />
            <Switch
              color='secondary'
              onChange={() => updateSettings('audioNotificationEnabled')}
              checked={audioNotificationEnabled}
            />
          </Stack>
          <Stack
            direction={'row'}
            spacing={2}
            justifyContent='space-between'
            alignItems={'center'}
          >
            <FormattedAlias id={'Banner notifications'} />
            <Switch
              color='secondary'
              onChange={() => updateSettings('bannerNotificationEnabled')}
              checked={bannerNotificationEnabled}
            />
          </Stack>
        </Stack>
        {Show_Alias_Sets_Select && (
          <>
            <Divider
              sx={{
                border: '1px solid rgb(217, 217, 217)',
                my: 2.5,
              }}
            />
            <Typography variant='subtitle1' gutterBottom>
              <FormattedAlias id={'Alias Sets'} />
            </Typography>

            <Box mt={3}>
              <Stack direction={'row'} spacing={3} alignItems='center'>
                <Box mt={2}>
                  <AliasSelector />
                </Box>
                <Box>
                  <Button
                    color='secondary'
                    startIcon={<FileUploadIcon />}
                    variant={'contained'}
                    disabled={isImporting}
                    onClick={handleImportClick}
                  >
                    <FormattedAlias id={'Import'} />
                  </Button>
                </Box>
                <Button
                  color='secondary'
                  startIcon={<DownloadIcon />}
                  variant={'outlined'}
                  disabled={isExporting}
                  onClick={() => handleExportClick()}
                >
                  <FormattedAlias id={'Export'} />
                </Button>
              </Stack>
            </Box>
            <input
              type='file'
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept='.csv'
              onChange={handleFileChange}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default storeConnector(SettingsDialog, {
  config: ['siteMeta'],
  service: [
    'showMap',
    'darkTheme',
    'timeMode',
    'audioNotificationEnabled',
    'bannerNotificationEnabled',
  ],
});
